import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DRLogo from "../MyImages/DRLogo.png";
import { buildContractPreviewPdf } from "../utils/contractPreview.mjs";
import { contractApiEndpoint } from "../utils/contractApi";
import "./contract-signing.css";

const CONTRACT_PATH =
  "/documents/SIMANOF_Dominican_Republic_2027_Contratos_Espanol.pdf";

const agreements = [
  "Audición y preparación musical",
  "Alquiler de instrumento",
  "Conducta y disciplina",
  "Habitaciones, playa, horario y supervisión",
  "Pagos, cancelaciones y reembolsos",
  "Documentos, llegada/salida y transporte",
  "Seguro internacional y bienes personales",
  "Salud, medicamentos y emergencias",
  "Imagen y política de grabación",
  "Modificaciones del programa, servicios y logística",
];

function todayInChicago() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

function makeSubmissionId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `contract-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function ageOnDate(dateOfBirth, referenceDate) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth || "")) return null;
  const [birthYear, birthMonth, birthDay] = dateOfBirth.split("-").map(Number);
  const [year, month, day] = referenceDate.split("-").map(Number);
  const parsedBirthDate = new Date(Date.UTC(birthYear, birthMonth - 1, birthDay));
  if (
    parsedBirthDate.getUTCFullYear() !== birthYear ||
    parsedBirthDate.getUTCMonth() !== birthMonth - 1 ||
    parsedBirthDate.getUTCDate() !== birthDay
  ) return null;
  let age = year - birthYear;
  if (month < birthMonth || (month === birthMonth && day < birthDay)) age -= 1;
  return age >= 0 && age <= 120 ? age : null;
}

function SignaturePad({ label, value, onChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const movedRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    context.scale(ratio, ratio);
    context.lineWidth = 2.35;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = "#102f55";
  }, []);

  const pointFor = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const begin = (event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const point = pointFor(event);
    drawingRef.current = true;
    movedRef.current = false;
    canvas.setPointerCapture(event.pointerId);
    context.beginPath();
    context.moveTo(point.x, point.y);
  };

  const draw = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const context = canvasRef.current.getContext("2d");
    const point = pointFor(event);
    movedRef.current = true;
    context.lineTo(point.x, point.y);
    context.stroke();
  };

  const finish = (event) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (canvasRef.current.hasPointerCapture(event.pointerId)) {
      canvasRef.current.releasePointerCapture(event.pointerId);
    }
    if (movedRef.current) onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div className={`signature-field ${value ? "is-signed" : ""}`}>
      <div className="signature-heading">
        <div>
          <span className="field-label">{label}</span>
          <p>Firma dentro del recuadro usando el dedo, mouse o trackpad.</p>
        </div>
        <button type="button" onClick={clear} className="text-button">
          Borrar
        </button>
      </div>
      <div className="signature-canvas-wrap">
        <canvas
          ref={canvasRef}
          aria-label={label}
          onPointerDown={begin}
          onPointerMove={draw}
          onPointerUp={finish}
          onPointerCancel={finish}
        />
        {!value && <span className="signature-hint">Firme aquí</span>}
        <span className="signature-line" />
      </div>
      <div className="signature-status" aria-live="polite">
        {value ? "Firma capturada" : "Firma pendiente"}
      </div>
    </div>
  );
}

export default function ContractSigning() {
  const signingDate = useMemo(todayInChicago, []);
  const [inviteToken] = useState(() => {
    const queryToken = new URLSearchParams(window.location.search).get("invite") || "";
    if (queryToken) window.sessionStorage.setItem("simanof-contract-invite", queryToken);
    return queryToken || window.sessionStorage.getItem("simanof-contract-invite") || "";
  });
  const [inviteState, setInviteState] = useState({ status: "loading", error: "" });
  const [form, setForm] = useState({
    participantName: "",
    participantEmail: "",
    dateOfBirth: "",
    guardianName: "",
    participantSignature: "",
    guardianSignature: "",
    consent: false,
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [contractSourceBytes, setContractSourceBytes] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(CONTRACT_PATH);
  const [previewState, setPreviewState] = useState("ready");
  const previewUrlRef = useRef("");
  const participantAge = useMemo(
    () => ageOnDate(form.dateOfBirth, signingDate),
    [form.dateOfBirth, signingDate],
  );
  const isMinor = participantAge !== null && participantAge < 18;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (new URLSearchParams(window.location.search).has("invite")) {
      window.history.replaceState({}, "", "/contratos");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!inviteToken) {
      setInviteState({ status: "invalid", error: "Necesitas el enlace privado que SIMANOF envió a tu correo." });
      return undefined;
    }
    fetch(contractApiEndpoint("verifyContractInvite"), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ inviteToken }),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "No se pudo validar la invitación.");
        return payload;
      })
      .then((payload) => {
        if (cancelled) return;
        setForm((current) => ({
          ...current,
          participantName: payload.participantName,
          participantEmail: payload.participantEmail,
        }));
        if (payload.completed) setReceipt(payload);
        setInviteState({ status: "valid", error: "" });
      })
      .catch((requestError) => {
        if (!cancelled) setInviteState({ status: "invalid", error: requestError.message });
      });
    return () => { cancelled = true; };
  }, [inviteToken]);

  useEffect(() => {
    let cancelled = false;
    fetch(CONTRACT_PATH)
      .then((response) => {
        if (!response.ok) throw new Error("No se pudo cargar el contrato.");
        return response.arrayBuffer();
      })
      .then((bytes) => {
        if (!cancelled) setContractSourceBytes(new Uint8Array(bytes));
      })
      .catch(() => {
        if (!cancelled) setPreviewState("error");
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!contractSourceBytes) return undefined;
    const hasPreviewData = Boolean(
      form.participantName.trim() ||
      form.participantSignature ||
      (isMinor && (form.guardianName.trim() || form.guardianSignature)),
    );

    if (!hasPreviewData) {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
      setPreviewUrl(CONTRACT_PATH);
      setPreviewState("ready");
      return undefined;
    }

    let cancelled = false;
    setPreviewState("updating");
    const timeout = window.setTimeout(async () => {
      try {
        const bytes = await buildContractPreviewPdf(contractSourceBytes, {
          participantName: form.participantName,
          participantSignature: form.participantSignature,
          guardianName: isMinor ? form.guardianName : "",
          guardianSignature: isMinor ? form.guardianSignature : "",
          isMinor,
          signingDate,
        });
        if (cancelled) return;
        const nextUrl = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = nextUrl;
        setPreviewUrl(nextUrl);
        setPreviewState("ready");
      } catch (previewError) {
        if (!cancelled) setPreviewState("error");
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [contractSourceBytes, form.guardianName, form.guardianSignature, form.participantName, form.participantSignature, isMinor, signingDate]);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const update = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    setError("");
  };

  const validate = () => {
    if (form.participantName.trim().length < 2) return "Escribe el nombre completo del participante.";
    if (!/^\S+@\S+\.\S+$/.test(form.participantEmail.trim())) return "Escribe un correo electrónico válido.";
    if (participantAge === null) return "Escribe una fecha de nacimiento válida.";
    if (!form.participantSignature) return "La firma del participante es obligatoria.";
    if (isMinor && form.guardianName.trim().length < 2) return "Escribe el nombre completo del padre, madre o tutor.";
    if (isMinor && !form.guardianSignature) return "La firma del padre, madre o tutor es obligatoria para menores.";
    if (!form.consent) return "Debes aceptar la declaración de firma electrónica antes de continuar.";
    return "";
  };

  const submit = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError("");
    const submissionId = makeSubmissionId();
    const endpoint = contractApiEndpoint("submitContract");

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          inviteToken,
          clientSubmissionId: submissionId,
          participantName: form.participantName.trim(),
          participantEmail: form.participantEmail.trim().toLowerCase(),
          dateOfBirth: form.dateOfBirth,
          guardianName: isMinor ? form.guardianName.trim() : "",
          participantSignature: form.participantSignature,
          guardianSignature: isMinor ? form.guardianSignature : "",
          acceptedAgreementIds: agreements.map((_, index) => index + 1),
          consent: form.consent,
          website: form.website,
          displayedSigningDate: signingDate,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "No se pudo completar el envío. Inténtalo nuevamente.");
      setReceipt(payload);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (requestError) {
      setError(requestError.message || "No se pudo completar el envío.");
    } finally {
      setSubmitting(false);
    }
  };

  if (inviteState.status === "loading") {
    return (
      <main className="contract-page receipt-page">
        <div className="receipt-card access-message-card">
          <span className="spinner dark" />
          <p className="eyebrow">ENLACE PRIVADO</p>
          <h1>Validando tu invitación…</h1>
          <p>Estamos confirmando que este enlace fue emitido por SIMANOF.</p>
        </div>
      </main>
    );
  }

  if (inviteState.status === "invalid") {
    return (
      <main className="contract-page receipt-page">
        <div className="receipt-card access-message-card">
          <div className="access-lock" aria-hidden="true">🔒</div>
          <p className="eyebrow">ACCESO POR INVITACIÓN</p>
          <h1>Este contrato no está abierto al público.</h1>
          <p>{inviteState.error}</p>
          <div className="receipt-actions">
            <Link to="/" className="secondary-action">Volver al inicio</Link>
          </div>
        </div>
      </main>
    );
  }

  if (receipt) {
    return (
      <main className="contract-page receipt-page">
        <div className="contract-glow contract-glow-one" />
        <div className="receipt-card">
          <div className="success-mark" aria-hidden="true">✓</div>
          <p className="eyebrow">FIRMA COMPLETADA</p>
          <h1>Tu copia firmada está en camino.</h1>
          <p>
            Enviamos el PDF firmado a <strong>{form.participantEmail}</strong> y una copia a
            SIMANOF. Conserva el correo como recibo de finalización.
          </p>
          <div className="confirmation-number">
            <span>NÚMERO DE CONFIRMACIÓN</span>
            <strong>{receipt.confirmationId || receipt.submissionId}</strong>
          </div>
          <div className="receipt-actions">
            <Link to="/" className="secondary-action">Volver al inicio</Link>
            <a href={CONTRACT_PATH} target="_blank" rel="noreferrer" className="primary-action">
              Ver contrato original
            </a>
          </div>
          {(receipt.musicUrl || receipt.musicLinks || receipt.itineraryUrl) && (
            <div className="festival-resources">
              <span>MATERIALES DEL FESTIVAL</span>
              <div>
                {receipt.musicLinks?.orchestra && <a href={receipt.musicLinks.orchestra} target="_blank" rel="noreferrer" className="primary-action">Orchestra</a>}
                {receipt.musicLinks?.ensemble && <a href={receipt.musicLinks.ensemble} target="_blank" rel="noreferrer" className="primary-action">{receipt.musicLinks.ensembleLabel || "Ensemble"}</a>}
                {receipt.musicLinks?.technique && <a href={receipt.musicLinks.technique} target="_blank" rel="noreferrer" className="primary-action">Technique</a>}
                {receipt.musicUrl && <a href={receipt.musicUrl} target="_blank" rel="noreferrer" className="primary-action">Escuchar música</a>}
                {receipt.itineraryUrl && <a href={receipt.itineraryUrl} target="_blank" rel="noreferrer" className="secondary-action">Descargar itinerario PDF</a>}
              </div>
            </div>
          )}
          <p className="receipt-note">Si no ves el mensaje, revisa las carpetas de spam o promociones.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="contract-page">
      <div className="contract-glow contract-glow-one" />
      <div className="contract-glow contract-glow-two" />

      <header className="contract-topbar">
        <Link to="/" className="contract-brand">
          <img src={DRLogo} alt="SIMANOF Festival República Dominicana" />
          <span><strong>SIMANOF</strong><small>Centro seguro de firmas</small></span>
        </Link>
        <div className="secure-pill"><span>✓</span> Firma protegida</div>
      </header>

      <section className="contract-hero">
        <div>
          <p className="eyebrow">FESTIVAL REPÚBLICA DOMINICANA 2027</p>
          <h1>Revisa y firma tus acuerdos.</h1>
          <p className="hero-copy">
            Lee el documento completo. Tu firma se aplicará a los diez acuerdos y recibirás una copia final por correo.
          </p>
        </div>
        <ol className="signing-steps" aria-label="Pasos de firma">
          <li className="active"><span>1</span> Revisar</li>
          <li><span>2</span> Firmar</li>
          <li><span>3</span> Recibir</li>
        </ol>
      </section>

      <div className="contract-layout">
        <section className="document-panel">
          <div className="panel-heading">
            <div>
              <span className="panel-kicker">DOCUMENTO OFICIAL</span>
              <h2>Contratos del participante</h2>
            </div>
            <div className="panel-actions">
              <span className={`preview-status ${previewState}`} aria-live="polite">
                <span />
                {previewState === "updating"
                  ? "Actualizando vista"
                  : previewState === "error" ? "Vista original" : "Vista en vivo"}
              </span>
              <a href={CONTRACT_PATH} target="_blank" rel="noreferrer">Abrir PDF ↗</a>
            </div>
          </div>
          <div className="pdf-frame">
            <iframe title="Contratos SIMANOF República Dominicana 2027" src={`${previewUrl}#view=FitH&toolbar=1`} />
          </div>
          <div className="agreement-index">
            <p>Diez acuerdos incluidos</p>
            <ol>
              {agreements.map((agreement, index) => (
                <li key={agreement}><span>{String(index + 1).padStart(2, "0")}</span>{agreement}</li>
              ))}
            </ol>
          </div>
        </section>

        <form className="signing-panel" onSubmit={submit} noValidate>
          <div className="panel-heading compact">
            <div>
              <span className="panel-kicker">DATOS DEL FIRMANTE</span>
              <h2>Completa tu firma</h2>
            </div>
          </div>

          <div className="field-grid">
            <label className="form-field full">
              <span>Nombre completo del participante</span>
              <input
                value={form.participantName}
                readOnly
                aria-readonly="true"
                autoComplete="name"
                maxLength={120}
                placeholder="Nombre y apellido"
              />
            </label>
            <label className="form-field full">
              <span>Correo para recibir la copia firmada</span>
              <input
                type="email"
                value={form.participantEmail}
                readOnly
                aria-readonly="true"
                autoComplete="email"
                maxLength={254}
                placeholder="nombre@correo.com"
              />
              <small>Verificado por el enlace privado enviado a este correo.</small>
            </label>
            <label className="form-field">
              <span>Fecha de nacimiento</span>
              <input
                type="date"
                value={form.dateOfBirth}
                max={signingDate}
                onChange={(event) => update("dateOfBirth", event.target.value)}
                autoComplete="bday"
              />
              <small>Se utiliza únicamente para determinar si se requiere un tutor.</small>
            </label>
            <label className="form-field">
              <span>Fecha de firma</span>
              <input value={signingDate} readOnly aria-readonly="true" />
              <small>La hora exacta también se registrará al enviar.</small>
            </label>
            {participantAge !== null && (
              <div className={`age-status full ${isMinor ? "is-minor" : "is-adult"}`}>
                <span>{isMinor ? "Firma de tutor requerida" : "Participante adulto"}</span>
                <strong>
                  {isMinor
                    ? `Tiene ${participantAge} años. Se requiere el nombre y la firma del padre, madre o tutor.`
                    : `Tiene ${participantAge} años. No se requiere firma de padre, madre o tutor.`}
                </strong>
              </div>
            )}
          </div>

          <SignaturePad
            label="Firma del participante"
            value={form.participantSignature}
            onChange={(value) => update("participantSignature", value)}
          />

          {isMinor && (
            <div className="guardian-section">
              <p className="section-label">PADRE, MADRE O TUTOR LEGAL</p>
              <label className="form-field full">
                <span>Nombre completo</span>
                <input
                  value={form.guardianName}
                  onChange={(event) => update("guardianName", event.target.value)}
                  autoComplete="name"
                  maxLength={120}
                  placeholder="Nombre y apellido del tutor"
                />
              </label>
              <SignaturePad
                label="Firma del padre, madre o tutor"
                value={form.guardianSignature}
                onChange={(value) => update("guardianSignature", value)}
              />
            </div>
          )}

          <label className="consent-box">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(event) => update("consent", event.target.checked)}
            />
            <span>
              Confirmo que leí las diez páginas, que la información es correcta y que adopto esta firma electrónica
              para aceptar cada acuerdo. Entiendo que la firma tendrá el mismo efecto que mi firma manuscrita.
            </span>
          </label>

          <label className="honeypot" aria-hidden="true">
            Sitio web
            <input tabIndex="-1" autoComplete="off" value={form.website} onChange={(event) => update("website", event.target.value)} />
          </label>

          {error && <div className="form-error" role="alert">{error}</div>}

          <button type="submit" className="submit-contract" disabled={submitting}>
            {submitting ? <><span className="spinner" /> Preparando y enviando…</> : <>Finalizar y enviar copias <span>→</span></>}
          </button>
          <p className="privacy-note">
            El documento final se enviará de forma privada a tu correo y a SIMANOF. No publiques este enlace ni compartas tu firma con terceros.
          </p>
        </form>
      </div>
    </main>
  );
}
